import ThemeChip from "./ThemeChip";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import { AWP_DATA_FY_24_25, AWP_DATA_FY_25_26, AWP_DATA_FY_26_27 } from "../data/awpData";
import { useFilters } from "../context/FilterContext";

/**
 * Annual Work Plan (AWP) — Theme-wise Disbursement (FY 24-25 · FY 25-26 · FY 26-27).
 *
 * FY visibility rule (Feb 9, 2026):
 *   • When the sole selected FY is FY 2025-26 → FY 26-27 row is hidden.
 *   • Any other selection (FY 26-27, All Years, multi-FY) → all 3 rows visible.
 *
 * FY 26-27 progress-bar rule (Feb 9, 2026):
 *   • Always rendered as "Gross Actual Spend up to 31 Aug 26" progress against
 *     "Budget Estimates FY27" (theme-wise budget from Finance sheet, image 2).
 *   • The bar fill = actual / budget. Value cell shows "actual Cr / budget Cr".
 */

// FY 27 Budget Estimates (Total Projected Spends) — Finance sheet, Sep 2026.
// Keyed by canonical theme (post-remap in canonicalTheme()).
// Where the source split Education / Healthcare into sub-buckets we combined
// them per user direction ("you can combine those figures").
const FY27_BUDGET = {
  "Rural Upliftment":                     266,
  "Education":                            212,   // 152 (Other than TIIS) + 60 (TIIS)
  "Health":                               288,   // 130 (Other than Multi) + 159 (Multi-thematic); source displays 288 in bold
  "Water and Sanitation":                 101,
  "Nutrition":                            104,
  "Urban Poverty Alleviation":             62,
  "Arts, Culture and Creative Economies":  30,
  "Building Institutions":                103,
  "Cancer Care":                          308,
  "Others":                               117,
};

// FY 26-27 "Gross Actual Spend up to 31 Aug 26" — same Finance sheet.
// Overrides the values that would otherwise come from AWP_DATA_FY_26_27 so
// the bar reflects the latest Finance-approved actuals.
const FY2627_ACTUALS = {
  "Rural Upliftment":                      23,
  "Education":                             39,
  "Health":                                40,
  "Water and Sanitation":                   9,
  "Nutrition":                              4,
  "Urban Poverty Alleviation":              1,
  "Arts, Culture and Creative Economies":   3,
  "Building Institutions":                 30,
  "Cancer Care":                           38,
  "Others":                                 3,
};

const AWP_THEME_REMAP = {
  "Healthcare":                 "Health",
  "Arts":                       "Arts, Culture and Creative Economies",
  "Institutions & Others":      "Building Institutions",
  "Discretionary":              "Others",
  "Other (The AI 171 Memorial and Welfare Trust)": "Others",
};

const ARTS_THEME  = "Arts, Culture and Creative Economies";
const OTHERS_THEME = "Others";
const COLOR_24_25 = "#FCA5A5"; // red-300
const COLOR_25_26 = "#EF4444"; // red-500
const COLOR_26_27 = "#B71C1C"; // brand red

function canonicalTheme(awpTheme) {
  return AWP_THEME_REMAP[awpTheme] || awpTheme;
}

// Green (positive) · Faint-red (negative) · Grey (no baseline).
function growthTone(pct) {
  if (pct === null || Number.isNaN(pct)) return { cls: "bg-slate-100 text-slate-400 border border-slate-200", icon: Minus };
  if (pct >= 0)  return { cls: "bg-emerald-50 text-emerald-700 border border-emerald-200",  icon: ArrowUp };
  return { cls: "bg-rose-50 text-rose-700 border border-rose-200", icon: ArrowDown };
}

/** Growth badge (compact) — used for both Δ25vs24 and Δ26vs25. */
function GrowthCell({ pct, label }) {
  const tone = growthTone(pct);
  const Ic = tone.icon;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${tone.cls}`}>
        <Ic size={9} />
        {pct === null ? "—" : `${Math.abs(pct).toFixed(1)}%`}
      </span>
      <span className="text-[8px] text-slate-500 uppercase tracking-wide">{label}</span>
    </div>
  );
}

/** Mini sparkline strip: 3 mini-bars scaled to the row's max. */
function Sparkline({ v1, v2, v3 }) {
  const max = Math.max(v1, v2, v3, 1);
  const h = (v) => Math.max(3, (v / max) * 18);
  const trendUp   = v3 > v2 && v3 > v1;
  const trendDown = v3 < v2 && v3 < v1;
  const stroke = trendUp ? "#059669" : trendDown ? "#B71C1C" : "#94a3b8";
  return (
    <div className="flex items-end gap-0.5" style={{ height: 20 }}>
      <div style={{ width: 5, height: h(v1), backgroundColor: COLOR_24_25, borderRadius: 1 }} />
      <div style={{ width: 5, height: h(v2), backgroundColor: COLOR_25_26, borderRadius: 1 }} />
      <div style={{ width: 5, height: h(v3), backgroundColor: COLOR_26_27, borderRadius: 1 }} />
      <span className="ml-1 text-[10px] font-bold tabular-nums" style={{ color: stroke }}>
        {trendUp ? "↗" : trendDown ? "↘" : "→"}
      </span>
    </div>
  );
}

/** Compact row: three thin bars with inline value labels.
 *  When `showFy2627` is false the FY 26-27 sub-row is hidden.
 *  The FY 26-27 sub-row always renders as an "actual vs budget" progress bar. */
function CompactBars({ v2425, v2526, v2627, fy27Budget, maxVal, showFy2627 }) {
  const pct = (v) => maxVal > 0 ? Math.max(1, (v / maxVal) * 100) : 0;
  const rowClass = "flex items-center gap-2";
  const labelClass = "text-[9px] font-semibold uppercase tracking-wide tabular-nums";
  const valueClass = "text-[10px] font-semibold tabular-nums whitespace-nowrap";
  const progressPct = fy27Budget > 0 ? Math.min(100, (v2627 / fy27Budget) * 100) : 0;
  // Bar width (Ch13, Sep 10, 2026): extended from 240px to 400px so the
  // horizontal bars in the "Disbursement / AWP (₹ Cr)" column stretch further
  // and stay proportional to the column width in Financial-page renderings.
  const BAR_WIDTH = 400;
  return (
    <div className="space-y-1 py-0.5" data-testid="awp-stacked-bars">
      <div className={rowClass}>
        <span className={`${labelClass} text-slate-500`} style={{ width: 42 }}>24-25</span>
        <div className="relative h-2 rounded-full overflow-visible bg-slate-100/60" style={{ width: BAR_WIDTH }}>
          <div className="absolute top-0 left-0 h-full rounded-full transition-all duration-500"
            style={{ width: `${pct(v2425)}%`, backgroundColor: COLOR_24_25 }} />
        </div>
        <span className={`${valueClass} text-slate-700`} style={{ minWidth: 54 }}>
          {v2425 > 0 ? v2425.toFixed(2) : "—"} <span className="font-normal text-slate-400">Cr</span>
        </span>
      </div>
      <div className={rowClass}>
        <span className={`${labelClass} text-rose-500`} style={{ width: 42 }}>25-26</span>
        <div className="relative h-2 rounded-full overflow-visible bg-slate-100/60" style={{ width: BAR_WIDTH }}>
          <div className="absolute top-0 left-0 h-full rounded-full transition-all duration-500"
            style={{ width: `${pct(v2526)}%`, backgroundColor: COLOR_25_26 }} />
        </div>
        <span className={`${valueClass} text-slate-800`} style={{ minWidth: 54 }}>
          {v2526 > 0 ? v2526.toFixed(2) : "—"} <span className="font-normal text-slate-400">Cr</span>
        </span>
      </div>
      {showFy2627 && (
        <div className={rowClass} data-testid="awp-fy2627-progress">
          <span className={`${labelClass} text-[#B71C1C]`} style={{ width: 42 }}>26-27</span>
          <div
            className="relative h-2.5 rounded-full overflow-visible bg-slate-100 border border-slate-200"
            style={{ width: BAR_WIDTH }}
            title={fy27Budget > 0 ? `${v2627.toFixed(2)} Cr spent / ${fy27Budget} Cr budget (${progressPct.toFixed(1)}%)` : ""}
          >
            <div
              className="absolute top-0 left-0 h-full rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%`, backgroundColor: COLOR_26_27 }}
            />
            {fy27Budget > 0 && (
              <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-slate-700 tabular-nums pointer-events-none">
                {progressPct.toFixed(0)}%
              </span>
            )}
          </div>
          <span className={`${valueClass} text-slate-900`} style={{ minWidth: 78 }}>
            {v2627 > 0 ? v2627.toFixed(0) : "—"}
            {fy27Budget > 0 && (
              <>
                <span className="font-normal text-slate-400"> / </span>
                <span className="font-normal text-slate-500">{fy27Budget}</span>
              </>
            )}
            <span className="font-normal text-slate-400"> Cr</span>
          </span>
        </div>
      )}
    </div>
  );
}

// Aggregate a source array by canonical theme (handles Discretionary → Others fold).
function aggregate(rows) {
  const map = {};
  for (const r of rows) {
    const t = canonicalTheme(r.theme);
    map[t] = (map[t] || 0) + (r.utilisation || 0);
  }
  return map;
}

export default function AwpTable() {
  const { filters } = useFilters();
  const fyArr = Array.isArray(filters?.fy) ? filters.fy : (filters?.fy ? [filters.fy] : []);
  const soleFy2526 = fyArr.length === 1 && fyArr[0] === "FY 2025-26";
  const showFy2627 = !soleFy2526;

  const byTheme24 = aggregate(AWP_DATA_FY_24_25);
  const byTheme25 = aggregate(AWP_DATA_FY_25_26);
  const byTheme26 = aggregate(AWP_DATA_FY_26_27);
  const allThemes = Array.from(new Set([
    ...Object.keys(byTheme24), ...Object.keys(byTheme25), ...Object.keys(byTheme26),
    ...Object.keys(FY2627_ACTUALS),
  ]));

  // Alphabetical, with Arts at the bottom and Others just above Arts.
  const specials = new Set([ARTS_THEME, OTHERS_THEME]);
  const sorted = allThemes.filter(t => !specials.has(t)).sort((a, b) => a.localeCompare(b));
  if (allThemes.includes(OTHERS_THEME)) sorted.push(OTHERS_THEME);
  if (allThemes.includes(ARTS_THEME))   sorted.push(ARTS_THEME);

  // Use Finance-approved FY 26-27 actuals when available; fall back to the
  // AWP_DATA_FY_26_27 aggregate otherwise (keeps older themes represented).
  const rows = sorted.map(theme => ({
    theme,
    v2425: byTheme24[theme] || 0,
    v2526: byTheme25[theme] || 0,
    v2627: FY2627_ACTUALS[theme] != null ? FY2627_ACTUALS[theme] : (byTheme26[theme] || 0),
    fy27Budget: FY27_BUDGET[theme] || 0,
  }));

  const total24 = rows.reduce((s, r) => s + r.v2425, 0);
  const total25 = rows.reduce((s, r) => s + r.v2526, 0);
  const total26 = rows.reduce((s, r) => s + r.v2627, 0);
  const totalFy27Budget = rows.reduce((s, r) => s + r.fy27Budget, 0);
  const maxVal = Math.max(...rows.flatMap(r => [r.v2425, r.v2526]), 1);
  const totalYoY_25_24 = total24 > 0 ? ((total25 - total24) / total24) * 100 : null;
  // Total-row 26-vs-25 growth compares FY 27 BUDGET total, not actual utilisation.
  const totalCompare26 = totalFy27Budget > 0 ? totalFy27Budget : total26;
  const totalYoY_26_25 = total25 > 0 ? ((totalCompare26 - total25) / total25) * 100 : null;

  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="awp-table-wrapper">
      <table className="min-w-full text-[11px]" data-testid="awp-table">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-1.5 px-3 text-[9px]" style={{ width: 200 }}>Theme</th>
            <th className="text-left font-semibold text-[#B71C1C] uppercase tracking-wide py-1.5 px-3 text-[9px]">Disbursement / AWP (₹ Cr)</th>
            <th className="text-center font-semibold text-slate-700 uppercase tracking-wide py-1.5 px-3 text-[9px]" style={{ width: 90 }}>Trend</th>
            <th className="text-center font-semibold text-slate-700 uppercase tracking-wide py-1.5 px-3 text-[9px]" style={{ width: 180 }}>Growth (YoY)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const g_25_24 = row.v2425 > 0 ? ((row.v2526 - row.v2425) / row.v2425) * 100 : null;
            // 26 vs 25 growth compares FY 27 BUDGET against FY 25-26 actuals
            // (all-three-years are budget-basis comparisons per stakeholder direction Feb 9, 2026).
            const compareVal26 = row.fy27Budget > 0 ? row.fy27Budget : row.v2627;
            const g_26_25 = row.v2526 > 0 ? ((compareVal26 - row.v2526) / row.v2526) * 100 : null;
            return (
              <tr
                key={row.theme}
                className={`border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/30"} hover:bg-red-50/30 transition-colors`}
                data-testid={`awp-row-${row.theme}`}
              >
                <td className="py-1.5 px-3 align-middle">
                  <ThemeChip theme={row.theme} size="xs" />
                </td>
                <td className="py-1.5 px-3 align-middle">
                  <CompactBars v2425={row.v2425} v2526={row.v2526} v2627={row.v2627} fy27Budget={row.fy27Budget} maxVal={maxVal} showFy2627={showFy2627} />
                </td>
                <td className="py-1.5 px-3 align-middle">
                  <div className="flex justify-center">
                    <Sparkline v1={row.v2425} v2={row.v2526} v3={showFy2627 ? compareVal26 : row.v2526} />
                  </div>
                </td>
                <td className="py-1.5 px-3 align-middle">
                  <div className="flex items-center justify-center gap-2">
                    <GrowthCell pct={g_25_24} label="FY 26 vs FY 25" />
                    {showFy2627 && <GrowthCell pct={g_26_25} label="FY 27 vs FY 26" />}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-300 bg-slate-50">
            <td className="py-1.5 px-3 text-[11px] font-bold text-slate-800 align-middle">Total</td>
            <td className="py-1.5 px-3 text-[11px] tabular-nums align-middle">
              <span className="text-slate-500">24-25</span>{" "}
              <span className="font-bold text-slate-700">{total24.toFixed(2)}</span>
              <span className="text-slate-400 mx-2">·</span>
              <span className="text-rose-500">25-26</span>{" "}
              <span className="font-bold text-slate-800">{total25.toFixed(2)}</span>
              {showFy2627 && (
                <>
                  <span className="text-slate-400 mx-2">·</span>
                  <span className="text-[#B71C1C]">26-27</span>{" "}
                  <span className="font-bold text-[#B71C1C]">{total26.toFixed(0)}</span>
                  {totalFy27Budget > 0 && (
                    <>
                      <span className="text-slate-400"> / </span>
                      <span className="text-slate-500">{totalFy27Budget} Budget</span>
                    </>
                  )}
                </>
              )}
              <span className="text-slate-500"> Cr</span>
            </td>
            <td className="py-1.5 px-3 align-middle">
              <div className="flex justify-center">
                <Sparkline v1={total24} v2={total25} v3={showFy2627 ? total26 : total25} />
              </div>
            </td>
            <td className="py-1.5 px-3 align-middle">
              <div className="flex items-center justify-center gap-2">
                <GrowthCell pct={totalYoY_25_24} label="FY 26 vs FY 25" />
                {showFy2627 && <GrowthCell pct={totalYoY_26_25} label="FY 27 vs FY 26" />}
              </div>
            </td>
          </tr>
        </tfoot>
      </table>

      {/* Footer legend */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50/50 border-t border-slate-200 text-[10px]">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-2 rounded-sm" style={{ backgroundColor: COLOR_24_25 }} />
            <span className="font-semibold text-slate-700">FY 24-25</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-2 rounded-sm" style={{ backgroundColor: COLOR_25_26 }} />
            <span className="font-semibold text-rose-500">FY 25-26</span>
          </span>
          {showFy2627 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-2 rounded-sm" style={{ backgroundColor: COLOR_26_27 }} />
              <span className="font-semibold text-[#B71C1C]">FY 26-27 (Actual / Budget)</span>
            </span>
          )}
        </div>
        <span className="italic text-slate-500">
          <span className="text-emerald-700">▲</span> Positive growth ·{" "}
          <span className="text-rose-700">▼</span> Negative growth
        </span>
      </div>
    </div>
  );
}

export { AWP_DATA_FY_24_25, AWP_DATA_FY_25_26, AWP_DATA_FY_26_27 };
